import React from 'react';
import { useTranslation } from 'utils/i18n';
import { StaticPageShell } from 'feSharedIndex';

// Import extracted logic
import translations from './translations';

/**
 * Testimonials Component
 * Displays customer testimonials. Uses shared StaticPageShell; theme from config.
 */
const Testimonials = () => {
  const { language } = useTranslation();

  const t = (key, defaultValue = '') => {
    const pageTranslations = translations[language] || translations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      role: "CEO",
      company: "TechStart Solutions",
      image: "/resources/images/testimonials/sarah-johnson.jpg",
      rating: 5,
      quote: "B-esper's chatbot solution has revolutionized our customer service. The implementation was seamless, and our response times have improved by 80%. The AI understands context perfectly and provides accurate, helpful responses.",
      industry: "Technology",
      useCase: "Customer Support",
      results: "80% faster response times, 95% customer satisfaction"
    },
    {
      id: 2,
      name: "Michael Chen",
      role: "Operations Director",
      company: "Global Manufacturing Corp",
      image: "/resources/images/testimonials/michael-chen.jpg",
      rating: 5,
      quote: "We needed a solution that could handle multiple languages and complex technical queries. B-esper delivered beyond our expectations. The chatbot handles 90% of our inquiries without human intervention.",
      industry: "Manufacturing",
      useCase: "Technical Support",
      results: "90% automation rate, 24/7 availability"
    },
    {
      id: 3,
      name: "Emma Rodriguez",
      role: "Marketing Manager",
      company: "Retail Plus",
      image: "/resources/images/testimonials/emma-rodriguez.jpg",
      rating: 5,
      quote: "The integration with our existing systems was incredibly smooth. Our sales team loves how the chatbot qualifies leads and schedules appointments. It's like having an extra team member working 24/7.",
      industry: "Retail",
      useCase: "Lead Generation",
      results: "40% increase in qualified leads, 60% time savings"
    },
    {
      id: 4,
      name: "David Thompson",
      role: "IT Director",
      company: "Financial Services Group",
      image: "/resources/images/testimonials/david-thompson.jpg",
      rating: 5,
      quote: "Security and compliance were our top concerns. B-esper's solution not only meets all our regulatory requirements but also provides excellent user experience. Our customers trust the system completely.",
      industry: "Financial Services",
      useCase: "Compliance & Security",
      results: "100% compliance, zero security incidents"
    },
    {
      id: 5,
      name: "Lisa Wang",
      role: "Head of Customer Success",
      company: "Healthcare Innovations",
      image: "/resources/images/testimonials/lisa-wang.jpg",
      rating: 5,
      quote: "The healthcare industry has unique challenges, but B-esper's team understood our needs perfectly. The chatbot handles patient inquiries with empathy and accuracy, maintaining HIPAA compliance throughout.",
      industry: "Healthcare",
      useCase: "Patient Support",
      results: "85% patient satisfaction, 100% HIPAA compliance"
    },
    {
      id: 6,
      name: "James Wilson",
      role: "Founder",
      company: "EduTech Solutions",
      image: "/resources/images/testimonials/james-wilson.jpg",
      rating: 5,
      quote: "As a startup, we needed an affordable solution that could scale with us. B-esper's pricing model is perfect, and the chatbot has grown with our business. It's been a game-changer for our student support.",
      industry: "Education",
      useCase: "Student Support",
      results: "300% growth support, 50% cost reduction"
    }
  ];

  const stats = [
    {
      number: "500+",
      label: "Happy Customers",
      description: "Companies using our solutions"
    },
    {
      number: "98%",
      label: "Satisfaction Rate",
      description: "Customer satisfaction score"
    },
    {
      number: "24/7",
      label: "Support Available",
      description: "Round-the-clock assistance"
    },
    {
      number: "50+",
      label: "Countries",
      description: "Global presence"
    }
  ];

  const industries = [
    { name: "Technology", count: 150, icon: "💻" },
    { name: "Healthcare", count: 120, icon: "🏥" },
    { name: "Financial Services", count: 95, icon: "💰" },
    { name: "Retail", count: 80, icon: "🛍️" },
    { name: "Manufacturing", count: 70, icon: "🏭" },
    { name: "Education", count: 60, icon: "🎓" },
    { name: "Government", count: 45, icon: "🏛️" },
    { name: "Non-Profit", count: 35, icon: "🤝" }
  ];

  return (
    <StaticPageShell
      title={t('title', 'Testimonials')}
      lead={t('subtitle', 'What our customers say about B-esper')}
      contentClassName="static-page-content"
    >
      <section className="content-section">
          <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--spacing-lg)', marginBottom: 'var(--spacing-3xl)' }}>
            {stats.map((stat, index) => (
              <div key={index} className="stat-card" style={{ textAlign: 'center', padding: 'var(--spacing-lg)', background: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}>
                <div className="stat-number" style={{ fontSize: 'var(--text-4xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-primary)', marginBottom: 'var(--spacing-sm)' }}>
                  {stat.number}
                </div>
                <div className="stat-label" style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--spacing-xs)' }}>
                  {stat.label}
                </div>
                <div className="stat-description" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="content-section">
          <h2>{t('testimonialsGrid.title')}</h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <div className="testimonial-header">
                  <div className="testimonial-image">
                    <img src={testimonial.image} alt={testimonial.name} />
                  </div>
                  <div className="testimonial-info">
                    <h3>{testimonial.name}</h3>
                    <p className="testimonial-role">{testimonial.role}, {testimonial.company}</p>
                    <div className="testimonial-rating">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i}>★</span>
                      ))}
                    </div>
                  </div>
                </div>
                <blockquote className="testimonial-quote">{testimonial.quote}</blockquote>
                <div className="testimonial-meta">
                  <span className="testimonial-industry">{testimonial.industry}</span>
                  <span className="testimonial-usecase">{testimonial.useCase}</span>
                </div>
                <div className="testimonial-results">
                  <strong>Results:</strong> {testimonial.results}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="content-section">
          <h2>{t('industries.title')}</h2>
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div key={index} className="industry-card">
                <div className="industry-icon">{industry.icon}</div>
                <h3>{industry.name}</h3>
                <p>{industry.count} customers</p>
              </div>
            ))}
          </div>
        </section>
    </StaticPageShell>
  );
};

export default Testimonials;
